import PageNavigation from "../components/navigation/pageNavigation";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Image from "next/image";
import ReactMarkdown from "react-markdown";
import PageFooter from "@components/navigation/pageFooter";
import { NextSeo } from "next-seo";
import PageHeader from "@components/pageHeader";
import { ArrowNarrowRightIcon } from "@heroicons/react/solid";
import Link from "next/link";

const About = ({ about }) => {
  const { t } = useTranslation(["common", "about"]);

  const getWhyUs = (id, border = true) => {
    return (
      <>
        <h4 className="mt-4">{t(`about:why.${id}.header`)}</h4>
        <p
          className={
            "py-2 pl-6 mt-2 ml-2 md:w-3/4 " +
            (border && "border-l-2 border-default")
          }
        >
          {t(`about:why.${id}.description`)}
        </p>
      </>
    );
  };

  return (
    <>
      <NextSeo title="Abouts us" />
      <PageNavigation />
      <PageHeader title={t("navigation.about")}>
        <ReactMarkdown className="whitespace-pre-wrap">{about}</ReactMarkdown>
      </PageHeader>
      <section className="mt-10 c-container">
        <h1 className="text-4xl font-semibold">{t("about:what-we-do")}</h1>
        <div className="grid grid-cols-1 grid-rows-3 gap-4 mt-8 md:grid-cols-2">
          <div className="relative col-span-1 rounded md:row-span-2">
            <Image
              src="/assets/about/lptex-services-1.jpeg"
              layout="fill"
              objectFit="cover"
              className="relative z-0 rounded"
            />
            <div className="relative z-10 flex items-end w-full h-full p-6 text-white bg-black bg-opacity-50 dark:bg-opacity-70 aspect-[2/1] md:aspect-square rounded">
              <p className="text-xl font-semibold">{t("about:services.1")}</p>
            </div>
          </div>
          <div className="relative col-span-1 row-span-1 rounded">
            <Image
              src="/assets/about/lptex-services-2.jpeg"
              layout="fill"
              objectFit="cover"
              className="relative z-0 rounded"
            />
            <div className="relative z-10 flex items-end h-full w-full p-6 text-white bg-black bg-opacity-50 dark:bg-opacity-70 aspect-[2/1] rounded">
              <p className="text-xl font-semibold">{t("about:services.2")}</p>
            </div>
          </div>
          <div className="relative col-span-1 rounded md:row-span-2">
            <Image
              src="/assets/about/lptex-services-3.jpeg"
              layout="fill"
              objectFit="cover"
              className="relative z-0 rounded"
            />
            <div className="relative z-10 flex items-end w-full h-full p-6 text-white bg-black bg-opacity-50 dark:bg-opacity-70 aspect-[2/1] md:aspect-square rounded">
              <p className="text-xl font-semibold">{t("about:services.3")}</p>
            </div>
          </div>
          <div className="relative col-span-1 row-span-1 rounded">
            <Image
              src="/assets/about/lptex-services-4.jpeg"
              layout="fill"
              objectFit="cover"
              className="relative z-0 rounded"
            />
            <div className="relative z-10 flex items-end h-full w-full p-6 text-white bg-black bg-opacity-50 dark:bg-opacity-70 aspect-[2/1] rounded">
              <p className="text-xl font-semibold">{t("about:services.4")}</p>
            </div>
          </div>
        </div>
      </section>
      <aside className="mt-10 c-container !py-0">
        <Link href="/documents">
          <a className="block rounded bg-default !p-8">
            <article className="text-center md:text-left">
              <h1 className="text-2xl font-semibold text-white">
                {t("about:cert.header")}
              </h1>
              <p className="flex items-center justify-center text-white md:justify-start">
                {t("about:cert.description")}
                <ArrowNarrowRightIcon className="w-5 h-5 ml-1" />
              </p>
            </article>
          </a>
        </Link>
      </aside>
      <section className="md:mt-10 c-container">
        <div className="grid grid-cols-1 md:gap-10 md:grid-cols-2">
          <div className="relative">
            <Image
              src="/assets/about/why-us.jpeg"
              layout="fill"
              objectFit="cover"
              className="rounded"
            />
          </div>
          <div className="py-10">
            <h1 className="text-4xl font-semibold">{t("about:why.heading")}</h1>
            <p className="mt-6">{t("about:why.subtitle")}</p>
            <article className="mt-6">
              {getWhyUs(1)}
              {getWhyUs(2)}
              {getWhyUs(3)}
              {getWhyUs(4, false)}
            </article>
          </div>
        </div>
      </section>
      <PageFooter />
    </>
  );
};

export default About;

export async function getStaticProps({ locale }) {
  const baseUrl = process.env.STRAPI_URL;

  try {
    const responseAbout = await fetch(`${baseUrl}/api/about?locale=${locale}`, {
      headers: {
        Authorization: `Bearer ${process.env.STRAPI_KEY}`,
      },
    });

    let about = await responseAbout.json();
    about = about.data.attributes.text;

    return {
      props: {
        about,
        baseUrl,
        ...(await serverSideTranslations(locale, ["common", "about"])),
      },
      revalidate: 60,
    };
  } catch (error) {
    console.log(error);

    return null;
  }
}
